import type { World } from '../sim/types';
import { deserialise, serialise } from '../sim/save';

/**
 * Where saves live: the browser's `localStorage`, one key per slot. Platform
 * code, kept out of `src/sim` so a packaged build can swap the storage without
 * touching the simulation. `src/sim/save.ts` turns a world into bytes and back;
 * this file only knows where the bytes go.
 */

const PREFIX = 'seven-seas.slot.';
export const AUTOSAVE_SLOT = 'auto';
export const QUICK_SLOT = 'quick';
/** Where an autosave the game could not read is kept (`keepSlot`). */
export const UNREADABLE_SLOT = 'unreadable';

export function writeSlot(slot: string, world: World): string {
  try {
    localStorage.setItem(PREFIX + slot, serialise(world, new Date().toISOString()));
    return 'Saved.';
  } catch {
    return 'Could not save: the browser refused the storage.';
  }
}

/** The message for an empty slot: nothing to keep or to tell. */
export const NO_SAVE = 'There is no save to load.';

/**
 * A slot's bytes kept aside in another (review 29: an autosave the game could not read, a newer version's or a damaged
 * one, was overwritten by the new game within thirty seconds; kept, a later build can open it). False where the
 * storage refused, or the slot is empty.
 */
export function keepSlot(slot: string, aside: string): boolean {
  try {
    const json = localStorage.getItem(PREFIX + slot);
    if (json === null) return false;
    localStorage.setItem(PREFIX + aside, json);
    return true;
  } catch {
    return false;
  }
}

/** The world in the slot, or the reason there is none. */
export function readSlot(slot: string): World | string {
  let json: string | null;
  try {
    json = localStorage.getItem(PREFIX + slot);
  } catch {
    return 'Could not read saves: the browser refused the storage.';
  }
  if (json === null) return NO_SAVE;
  try {
    return deserialise(json);
  } catch (error) {
    if (error instanceof SyntaxError) return 'That save is not readable.';
    return (error as Error).message;
  }
}
